/**
 * Insights — Cost & Capacity tab
 *
 * Honest version. Real data: /insights-module/summary,
 * /insights-module/time-series (cost + latency averages), and
 * /insights-module/top-models for per-model usage. Sections that
 * have no backend (cost treemap, team budgets, GPU/VRAM, queue
 * depth, p50/p95/p99, rate-limited users, cache hit rate, TPM)
 * are rendered as empty-state placeholders.
 *
 * Backend gaps tracked separately — see MOCK-INVENTORY-2026-05-06.md.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  DollarSign, Cpu, Clock, Activity, Hash, Database,
  AlertCircle, Gauge, Zap,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, Legend,
} from 'recharts';
import {
  KpiCard, ChartCard, FilterBar, ActionItemsDrawer, EmptySection,
  filtersToParams, rangeLabel, rangeEmptyStatePhrase,
  useInsightsSearchFilters, validateInsightsSearch,
} from '@/components/insights';
import {
  useInsightsTimeSeries,
  useInsightsSummary,
  useTopModels,
} from '@/lib/use-insights';

export const Route = createFileRoute('/insights/cost')({
  component: CostCapacityTab,
  // INS-FILTER-PERSIST-001 — URL-bind filter state per resolved decision #9.
  validateSearch: validateInsightsSearch,
});

/**
 * R3-P0 defect #5 — dollar Y-axis tick formatter that does NOT floor
 * sub-$10 values to "$0". Recharts' tickFormatter receives the
 * computed axis tick value (number), so we just need to choose the
 * right precision.
 *
 * Exported so the unit test can assert on the boundary cases. Behaviour:
 *   -    0     → "$0.00"
 *   -    0.43  → "$0.43"   (was "$0" before this fix)
 *   -    9.99  → "$9.99"
 *   -   10     → "$10"
 *   -  153.7   → "$154"    (integer rounding for legibility above $10)
 */
export function dollarTickFormatter(v: number): string {
  return v < 10 ? `$${Number(v).toFixed(2)}` : `$${Math.round(v)}`;
}

function CostCapacityTab() {
  const [filters, setFilters] = useInsightsSearchFilters('/insights/cost');
  const [actionItemsOpen, setActionItemsOpen] = useState(false);

  // R3-P0 defect #1 — FilterBar filters propagate to backend.
  const filterParams = filtersToParams(filters);
  // Pod-KK #111 — labels follow the FilterBar range.
  const rl = rangeLabel(filters.range);
  // Bug #1117 — full-sentence range phrase for empty-state copy. The
  // prior hardcoded "in the last 30 days" contradicted the live
  // headings ("Daily spend (7d)" etc) whenever the operator selected
  // 24h / 7d / 90d / custom.
  const rp = rangeEmptyStatePhrase(filters.range);
  // #1021 — surface the summary error explicitly so a stuck/failed
  // /insights-module/summary call no longer leaves the KPI cards in
  // perpetual '—' with no operator signal. The Overview tab and this
  // tab share the same react-query cache key, so they render in lock-step
  // once data arrives; the error/loading display logic must be
  // consistent between them too.
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrorObj,
  } = useInsightsSummary(filterParams);
  const { data: costSeries = [] } = useInsightsTimeSeries('cost', 30, filterParams);
  const { data: latencySeries = [] } = useInsightsTimeSeries('latency', 30, filterParams);
  // Bug #1118 — per-day latency percentiles for the (now real) p50/p95/p99
  // chart. The mean-latency series above is kept for the "Average latency"
  // card, but the headline KPI + percentile chart use these because the
  // mean is dragged into the hundreds of seconds by hung-query outliers.
  const { data: p50Series = [] } = useInsightsTimeSeries('latency_p50', 30, filterParams);
  const { data: p95Series = [] } = useInsightsTimeSeries('latency_p95', 30, filterParams);
  const { data: p99Series = [] } = useInsightsTimeSeries('latency_p99', 30, filterParams);
  const { data: topModels = [] } = useTopModels(filterParams);

  const totalSpend30d = summary?.totalCostDollars ?? 0;
  const totalQueries = summary?.totalQueries ?? 0;
  const avgLatencyMs = summary?.avgLatencyMs ?? 0;
  // Bug #1118 — percentiles (median + tail) from /summary.
  const p50LatencyMs = summary?.p50LatencyMs ?? 0;
  const p95LatencyMs = summary?.p95LatencyMs ?? 0;
  const p99LatencyMs = summary?.p99LatencyMs ?? 0;
  const costPerQuery = totalQueries > 0 ? totalSpend30d / totalQueries : 0;

  const costData = costSeries.map((p) => ({ date: p.date, value: p.value }));
  const latencyData = latencySeries.map((p) => ({ date: p.date, value: p.value }));

  // Bug #1118 — merge the three percentile series by date into one dataset
  // for the multi-line p50/p95/p99 chart. Union the date keys so a day
  // present in one series but not another still renders (0-fill missing).
  const percentileByDate = new Map<
    string,
    { date: string; p50: number; p95: number; p99: number }
  >();
  const ensureRow = (date: string) => {
    let row = percentileByDate.get(date);
    if (!row) {
      row = { date, p50: 0, p95: 0, p99: 0 };
      percentileByDate.set(date, row);
    }
    return row;
  };
  for (const p of p50Series) ensureRow(p.date).p50 = p.value;
  for (const p of p95Series) ensureRow(p.date).p95 = p.value;
  for (const p of p99Series) ensureRow(p.date).p99 = p.value;
  const percentileData = Array.from(percentileByDate.values()).sort((a, b) =>
    a.date.localeCompare(b.date),
  );

  return (
    <div className="flex flex-col h-full">
      <FilterBar
        filters={filters}
        onChange={setFilters}
        actionItemsCount={0}
        onOpenActionItems={() => setActionItemsOpen(true)}
      />

      {/* Pod-KK #613 — explicit custom-range fallback notice. */}
      {filterParams.customFallback && (
        <div
          role="status"
          className="mx-6 mt-3 rounded-md border border-amber-300/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200"
        >
          Custom date ranges aren't supported yet — showing the last 7
          days. Pick a preset range to filter precisely.
        </div>
      )}

      <div className="flex-1 p-6 space-y-6">
        {/* #1021 — surface API failures explicitly so a stuck KPI is
            never mistaken for "no data". Same UX as the Overview hero. */}
        {summaryError && (
          <div
            role="alert"
            data-testid="insights-cost-summary-error"
            className="rounded-md border border-red-300/60 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-xs text-red-900 dark:text-red-200"
          >
            Couldn't load cost summary
            {summaryErrorObj instanceof Error && summaryErrorObj.message
              ? ` — ${summaryErrorObj.message}`
              : '.'}
          </div>
        )}

        {/* KPIs — real */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            label={`Spend (${rl})`}
            icon={DollarSign}
            value={summaryLoading ? '—' : `$${totalSpend30d.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
            numericValue={totalSpend30d}
            subtitle={`${totalQueries.toLocaleString()} queries`}
          />
          <KpiCard
            label="Cost / Query"
            icon={Hash}
            value={summaryLoading ? '—' : `$${costPerQuery.toFixed(4)}`}
            numericValue={costPerQuery}
            subtitle={`Spend ÷ queries (${rl})`}
          />
          {/* Bug #1118 — lead with the MEDIAN (p50) latency. The old
              "Avg Latency" headline showed the unweighted mean, which a
              handful of 30-minute wedged queries (Strix-Halo APU
              llama-swap incidents) dragged to ~265s @30d — physically
              implausible for a served query. p50 is the typical
              experience; p95 (tail) is surfaced in the subtitle, and the
              mean stays available on the "Average latency" trend chart
              below so nothing real is hidden. */}
          <KpiCard
            label={`Median Latency (${rl})`}
            icon={Clock}
            value={summaryLoading ? '—' : `${(p50LatencyMs / 1000).toFixed(2)}s`}
            numericValue={p50LatencyMs}
            subtitle={
              summaryLoading
                ? 'p50 · typical query'
                : `p95 ${(p95LatencyMs / 1000).toFixed(1)}s · mean ${(avgLatencyMs / 1000).toFixed(1)}s`
            }
          />
          <KpiCard
            label={`Total Tokens (${rl})`}
            icon={Activity}
            value={summaryLoading ? '—' : (summary?.totalTokens ?? 0).toLocaleString()}
            numericValue={summary?.totalTokens ?? 0}
            subtitle="Input + output"
          />
        </div>

        {/* Cost trend — real */}
        <ChartCard title={`Daily spend (${rl})`} icon={DollarSign}>
          {costData.length === 0 ? (
            <EmptySection
              message={`No cost data ${rp}.`}
              hint="Trend populates as query_metrics rows accumulate."
              minHeight="min-h-48"
            />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={costData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={50}
                    /*
                     * R3-P0 defect #5 — `tickFormatter={v => $${v.toFixed(0)}}`
                     * floored to integer dollars. See dollarTickFormatter
                     * above for the precision policy.
                     */
                    tickFormatter={dollarTickFormatter}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    formatter={(v: any) => `$${Number(v).toFixed(2)}`}
                  />
                  <Line type="monotone" dataKey="value" stroke="rgb(99 102 241)" strokeWidth={2} dot={false} name="Daily spend" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        {/* Top models — real */}
        <ChartCard title={`Top models (${rl})`} icon={Cpu}>
          {topModels.length === 0 ? (
            <EmptySection
              message={`No model usage ${rp}.`}
              hint="Per-model breakdown comes from query_metrics.model groupings."
              minHeight="min-h-48"
            />
          ) : (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topModels} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis dataKey="model" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={50} />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    formatter={(v: any, name: string) =>
                      name === 'totalCostDollars' ? `$${Number(v).toFixed(2)}` : Number(v).toLocaleString()
                    }
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Bar dataKey="queryCount" fill="rgb(99 102 241)" name="Queries" />
                  <Bar dataKey="totalCostDollars" fill="rgb(16 185 129)" name="Cost ($)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        {/* Avg latency trend — real (only mean is exposed) */}
        <ChartCard title={`Average latency (${rl})`} icon={Clock}>
          {latencyData.length === 0 ? (
            <EmptySection
              message={`No latency data ${rp}.`}
              hint="Per-day mean latency from query_metrics. p50/p95/p99 percentiles are not yet exposed."
              minHeight="min-h-48"
            />
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={latencyData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={50}
                    tickFormatter={(v) => `${(v / 1000).toFixed(1)}s`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    formatter={(v: any) => `${(Number(v) / 1000).toFixed(2)}s`}
                  />
                  <Line type="monotone" dataKey="value" stroke="rgb(16 185 129)" strokeWidth={2} dot={false} name="Avg latency" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        {/* Empty-state sections for missing backends */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title={`Cost breakdown by team & user (${rl})`} icon={DollarSign}>
            <EmptySection
              message="Per-team / per-user cost breakdown not yet available."
              hint="Requires joining query_metrics with users and a teams/groups taxonomy."
              minHeight="min-h-56"
            />
          </ChartCard>

          <ChartCard title="Budget tracking (current month)" icon={DollarSign}>
            <EmptySection
              message="Team budgets not wired."
              hint="Requires a per-team budget config, a monthly spend-rollup job, and forecast logic."
              minHeight="min-h-56"
            />
          </ChartCard>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="GPU / VRAM utilization" icon={Cpu}>
            <EmptySection
              message="GPU/VRAM telemetry not yet available."
              hint="Requires a metrics-scraper job pulling from the inference engine (vLLM / Ollama)."
              minHeight="min-h-48"
            />
          </ChartCard>

          <ChartCard title="GPU queue depth" icon={Gauge}>
            <EmptySection
              message="Queue-depth telemetry not yet available."
              hint="Same source as GPU/VRAM — exposed when the inference-engine metrics pipeline lands."
              minHeight="min-h-48"
            />
          </ChartCard>
        </div>

        {/* Bug #1118 — REAL percentile chart. Previously an honest
            placeholder ("Backend currently aggregates only the mean").
            /insights-module/time-series now exposes latency_p50/p95/p99
            via percentile_cont, so the operator can see the typical (p50)
            vs tail (p95/p99) latency per day — robust to the hung-query
            outliers that distort the mean. */}
        <ChartCard title={`Latency p50 / p95 / p99 (${rl})`} icon={Clock}>
          {percentileData.length === 0 ? (
            <EmptySection
              message={`No latency data ${rp}.`}
              hint="Per-day p50/p95/p99 from query_metrics.latency_ms (percentile_cont)."
              minHeight="min-h-48"
            />
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={percentileData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                    }
                  />
                  <YAxis
                    tick={{ fontSize: 10 }}
                    width={50}
                    tickFormatter={(v) => `${(v / 1000).toFixed(1)}s`}
                  />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 6, border: '1px solid hsl(var(--border))' }}
                    formatter={(v: any) => `${(Number(v) / 1000).toFixed(2)}s`}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Line type="monotone" dataKey="p50" stroke="rgb(16 185 129)" strokeWidth={2} dot={false} name="p50 (median)" />
                  <Line type="monotone" dataKey="p95" stroke="rgb(245 158 11)" strokeWidth={2} dot={false} name="p95" />
                  <Line type="monotone" dataKey="p99" stroke="rgb(239 68 68)" strokeWidth={2} dot={false} name="p99" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Rate-limited users (24h)" icon={AlertCircle}>
          <EmptySection
            message="Rate-limit hits not surfaced in Insights."
            hint="Limit decisions are taken at the gateway; needs a per-user counter exposed via an endpoint."
          />
        </ChartCard>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ChartCard title="Cache hit rate" icon={Database}>
            <EmptySection
              message="Cache-hit metrics not yet wired."
              hint="Requires the response cache to publish hit/miss counters."
            />
          </ChartCard>

          <ChartCard title="Tokens-per-minute utilization" icon={Zap}>
            <EmptySection
              message="TPM utilization not yet wired."
              hint="Requires a rolling-window aggregator over query_metrics or a gateway counter."
            />
          </ChartCard>
        </div>
      </div>

      <ActionItemsDrawer
        open={actionItemsOpen}
        onOpenChange={setActionItemsOpen}
        items={[]}
      />
    </div>
  );
}
